import {
    Alert,
    Autocomplete,
    Chip,
    Tooltip,
    TableContainer,
    Paper,
    Table,
    TableHead,
    TableCell,
    TableRow,
    TableBody,
    Checkbox,
    FormControlLabel,
    Typography,
    Avatar,
    TextField,
} from '@mui/material';
import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { useDispatch } from 'react-redux';
import { SNACKBAR_OPEN } from '../../store/actions';
import { ChangeEvent, memo } from "react";
import Swal from "sweetalert2";
import xslxSheetIcon from '../../assets/images';
import { read, utils } from "xlsx";
import NoNegative from "../textFieldNumber";
import MyDatePicker from "../MyDatePicker";

/** זמני: כש־`true` — טריגר טעינת אקסל (Chip) לא לחיץ ולא נפתח, כולל `doOpen`. */
export const UPLOAD_FROM_EXCEL_DISABLED = false;

type excelheader = "string" | "number" | "date" | "boolean";

export interface ExcelHeader {
    name: string;
    must: boolean;
    type: excelheader;
    column?: number;
    picker: any[];
    extractor?: (value: any) => string;
    defultValue?: any;
}

const translateType = (type: excelheader) => {
    switch (type) {
        case "string": return "טקסט";
        case "number": return "מספר";
        case "date": return "תאריך";
        case "boolean": return "בוליאני";
    }
};

const typeExample = (type: excelheader) => {
    switch (type) {
        case "string": return "טקסט - לדוגמא: יוני";
        case "number": return "מספר - לדוגמא: 123";
        case "date": return "תאריך - לדוגמא: 01/01/2021";
        case "boolean": return "בוליאני - לדוגמא: true/ false";
    }
};

function DialogUploadFile({
                              uploadCallback,
                              title,
                              body,
                              finishCallBack,
                              excelHeaders,
                              doOpen,
                              setDoOpen,
                              withDate,
                              showUpdateExistingCheckbox,
                              defaultUpdateExisting,
                              updateExistingLabel,
                              showIgnoreMustCheckbox,
                              defaultIgnoreMust,
                              chipLabel,
                              showPickerItems = false,
                          }: {
    uploadCallback?: any,
    title?: string,
    body?: ExcelHeader[],
    finishCallBack?: any,
    excelHeaders?: ExcelHeader[],
    doOpen?: boolean,
    setDoOpen?: (val: boolean) => void,
    withDate?: boolean,
    /** לקוחות/ספקים: כאשר מסומן — עדכון רשומות קיימות לפי מזהה/חיצוני/שם */
    showUpdateExistingCheckbox?: boolean,
    defaultUpdateExisting?: boolean,
    updateExistingLabel?: string,
    showIgnoreMustCheckbox?: boolean,
    defaultIgnoreMust?: boolean,
    /** תווית מקלעת (כמו Chip «משיכת מוצרים מממשק») */
    chipLabel?: string,
    /** כאשר `true` — שורת בחירה (picker) מעל העלאת הקובץ, לפי `excelHeaders` */
    showPickerItems?: boolean,
}) {
    const dispatch = useDispatch();
    const [open, setOpen] = React.useState(false);
    const [date, setDate] = React.useState(new Date());
    const [callbackDialog, setCallbackDialog] = React.useState(false);
    const [columnsSet, setColumns] = React.useState(body?.map(() => undefined) ?? []);
    const [pickerItems, setPickerItems] = React.useState(() => excelHeaders?.map(header => ({
        name: header.name,
        value: header.defultValue ?? "",
        options: header.picker,
        extractor: header.extractor,
        default: header.defultValue
    })) ?? []);
    const [callbackData, setCallbackData] = React.useState<any>(null);
    const [file, setFile] = React.useState<File>();
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const [skipFirstLine, setSkipFirstLine] = React.useState(false);
    const [updateExisting, setUpdateExisting] = React.useState(defaultUpdateExisting !== false);
    const [ignoreMust, setIgnoreMust] = React.useState(defaultIgnoreMust === true);
    // These flows send the raw file to the server, which parses and commits in one shot, so a
    // real row-by-row preview isn't possible here. As a safety net, at least show the row count
    // and require an explicit confirmation before the irreversible "load" click fires.
    const [fileRowCount, setFileRowCount] = React.useState<number | null>(null);
    const [confirmedLoad, setConfirmedLoad] = React.useState(false);

    React.useEffect(() => {
        if (UPLOAD_FROM_EXCEL_DISABLED) return;
        if (doOpen) {
            setOpen(doOpen);
            setDoOpen?.(false);
        }
    }, [doOpen]);

    React.useEffect(() => {
        if (!file) return;
        countDataRows(file).then(setFileRowCount);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [skipFirstLine]);

    const handleUploadClick = () => inputRef.current?.click();

    const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) return;
        const file = e.target.files[0];
        const isFileValid = await checkValidExcel(file);
        if (!isFileValid) {
            Swal.fire({ title: 'שגיאה', icon: 'error', text: 'קובץ לא תקין, אנא בדוק שכל עמודות החובה רשומות כנדרש' });
            return;
        }
        setFile(file);
        setConfirmedLoad(false);
        setFileRowCount(await countDataRows(file));
    };

    const countDataRows = async (file: File): Promise<number | null> => {
        try {
            const buffer = await file.arrayBuffer();
            const wb = read(buffer, { type: 'array' });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const rows = utils.sheet_to_json(ws, { header: 1, raw: true, defval: '' });
            if (!rows || rows.length === 0) return 0;
            return Math.max(0, rows.length - (skipFirstLine ? 2 : 1));
        } catch {
            return null;
        }
    };

    const checkValidExcel = async (file: File) => {
        if (!body) return true;
        const readTextWithEncoding = (file: File, encoding = 'windows-1255'): Promise<string> => {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result as string);
                reader.onerror = reject;
                reader.readAsText(file, encoding);
            });
        };

        const text = await readTextWithEncoding(file, 'windows-1255');

        let wb;

        if (text.startsWith('<HTML>') || text.includes('<TABLE')) {
            // Handle HTML masquerading as .xls
            wb = read(text, { type: 'string' });
        } else {
            const buffer = await file.arrayBuffer();
            wb = read(buffer, { type: 'array' });
        }

        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = utils.sheet_to_json(ws, { header: 1, raw: false });
        if (!rows || rows.length === 0) return false;

        const headers = skipFirstLine ? rows[1] : rows[0];
        console.log(rows)
        console.log(headers)
        const decoder = new TextDecoder('windows-1255');
        const headers2 = headers.map(h => decoder.decode(new Uint8Array(h.split('').map(c => c.charCodeAt(0)))));
        const normalizeHeaderCell = (h: unknown) =>
            String(h ?? '')
                .replace(/^\uFEFF/, '')
                .replace(/['"]/g, '')
                .trim();

        let columns: any = {};
        columnsSet.forEach((header, i) => {
            if (header != undefined && !isNaN(Number(header))) {
                columns[body[i].name] = Number(header);
            }
        });

        let eMust = [];
        console.log(columns)
        console.log(headers2)
        console.log(body)
        body.forEach(header => {
            let must = header.must;
            headers.forEach((h, i) => {
                if (header.name === normalizeHeaderCell(h)) {
                    if (columns[header.name] == undefined)
                        columns[header.name] = i;
                    if (header.must) {
                        must = false;
                    }
                }
            })
            headers2.forEach((h, i) => {
                if (header.name === normalizeHeaderCell(h)) {
                    if (columns[header.name] == undefined)
                        columns[header.name] = i;
                    if (header.must) {
                        must = false;
                    }
                }
            })
            if (must) {
                eMust.push(header.name);
            }


        })
        console.log(eMust)
        if (eMust.filter((f) => columns[f] == undefined).length > 0) {
            return false;
        }

        return columns;

    };

    const handleOk = async () => {
        if (!confirmedLoad) return;
        handleClose();
        try {
            if (file && uploadCallback) {
                const args: any = {};
                if (showPickerItems) {
                    pickerItems?.forEach(item => {
                        if (item.value != undefined && item.value !== "") args[item.name] = item.value;
                    });
                } else {
                    excelHeaders?.forEach((header) => {
                        const v = header.defultValue;
                        if (v !== undefined && v !== '') args[header.name] = v;
                    });
                }
                if (withDate) args.date = date;
                args.skip_first_line = skipFirstLine;
                args.cols = await checkValidExcel(file);
                if (showUpdateExistingCheckbox) {
                    args.update_existing = updateExisting;
                }
                if (showIgnoreMustCheckbox) {
                    args.ignore_must = ignoreMust;
                }

                const d = await uploadCallback(file, args);
                listSuccessfullUpload(typeof d === "string" ? JSON.parse(d.replaceAll("\\", "")) : d);

                dispatch({
                    type: SNACKBAR_OPEN,
                    open: true,
                    anchorOrigin: { vertical: 'top', horizontal: 'left' },
                    message: ` הקובץ נטען`,
                    variant: 'alert',
                    alertSeverity: 'success'
                });
            }
        } catch (e) {
            console.log(e);
            dispatch({
                type: SNACKBAR_OPEN,
                open: true,
                anchorOrigin: { vertical: 'top', horizontal: 'left' },
                message: `לא הצליח לטעון קובץ`,
                variant: 'alert',
                alertSeverity: 'error'
            });
        }
        if (finishCallBack) finishCallBack();
    };

    const handleClose = () => {
        setOpen(false);
        setFile(undefined);
        setFileRowCount(null);
        setConfirmedLoad(false);
    };

    const parseLoadXlsxResult = (raw: unknown): { line: number; status: 1 | 0; msg: string; score?: number }[] => {
        if (Array.isArray(raw)) return raw as { line: number; status: 1 | 0; msg: string; score?: number }[];
        if (typeof raw !== 'string') return [{ line: 0, status: 0, msg: 'תשובה לא צפויה מהשרת', score: 0 }];
        try {
            return JSON.parse(raw.replaceAll('\\', ''));
        } catch {
            const msg = raw.length > 800 ? `${raw.slice(0, 800)}…` : raw;
            return [{ line: 0, status: 0, msg: `לא ניתן לפרק כ-JSON: ${msg}`, score: 0 }];
        }
    };

    const listSuccessfullUpload = (callback: { line: number; status: 1 | 0; msg: string; score?: number }[]) => {
        setCallbackDialog(true);
        setCallbackData(callback.sort((a, b) => b.status - a.status));
    };

    return (
        <div>
            {doOpen == undefined && (
                <Tooltip
                    title={
                        UPLOAD_FROM_EXCEL_DISABLED
                            ? 'טעינה מאקסל — זמנית לא זמינה'
                            : chipLabel || 'לטעון מאקסל'
                    }
                >
                    <span style={{ display: 'inline-flex' }}>
                        <Chip
                            disabled={UPLOAD_FROM_EXCEL_DISABLED}
                            onClick={() => !UPLOAD_FROM_EXCEL_DISABLED && setOpen(true)}
                            variant='outlined'
                            label={chipLabel || 'לטעון מאקסל'}
                            icon={
                                <Avatar
                                    variant='square'
                                    src={xslxSheetIcon}
                                    alt='Excel'
                                    sx={{
                                        width: 24,
                                        height: 24,
                                        mr: 0,
                                        ml: 0,
                                        p: 0,
                                        bgcolor: 'transparent',
                                        '& img': { objectFit: 'contain' },
                                    }}
                                />
                            }
                            sx={{
                                ml: 0.5,
                                mr: 0.5,
                                cursor: UPLOAD_FROM_EXCEL_DISABLED ? 'default' : 'pointer',
                                borderColor: '#008000',
                                color: '#008000',
                                '& .MuiChip-icon': { color: '#008000' },
                            }}
                        />
                    </span>
                </Tooltip>
            )}

            {open && (
                <Dialog maxWidth='sm' fullWidth open={open} onClose={handleClose}>
                    <DialogTitle>{title || 'טען קובץ'}</DialogTitle>
                    <DialogContent>
                        <TableContainer component={Paper}>
                            <Table>
                                <TableBody>
                                    {body?.map((header, index) => (
                                        <TableRow key={header.name}>
                                            <TableCell>{header.name}</TableCell>
                                            <TableCell>
                                                <NoNegative
                                                    id='head'
                                                    fullWidth
                                                    label='עמודה'
                                                    value={columnsSet[index]}
                                                    changer={(v) => {
                                                        const newCols = [...columnsSet];
                                                        newCols[index] = v;
                                                        setColumns(newCols);
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>{header.must ? "חובה" : "לא חובה"}</TableCell>
                                            <TableCell>
                                                <Tooltip title={typeExample(header.type)}>
                                                    <span>{translateType(header.type)}*</span>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {showPickerItems && pickerItems.length > 0 ? (
                            <>
                                <br />
                                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                                    {pickerItems.map((item, index) => (
                                        <Autocomplete
                                            key={index}
                                            options={item.options}
                                            getOptionLabel={(option) => item.extractor ? item.extractor(option) : option}
                                            multiple={!!item.extractor}
                                            fullWidth
                                            onChange={(e, value) => {
                                                const updated = [...pickerItems];
                                                updated[index].value = value || item.default;
                                                setPickerItems(updated);
                                            }}
                                            renderInput={(params) => <TextField {...params} label={item.name} />}
                                        />
                                    ))}
                                </div>
                            </>
                        ) : null}

                        <br />
                        <button onClick={handleUploadClick}>{file ? file.name : 'לחץ כאן כדי לעלות'}</button>
                        <input type='file' ref={inputRef} onChange={handleFileChange} style={{ display: 'none' }} />

                        <FormControlLabel
                            control={<Checkbox checked={skipFirstLine} onChange={(e) => setSkipFirstLine(e.target.checked)} />}
                            label='דלג על שורת כותרת ראשונה'
                        />

                        {showUpdateExistingCheckbox ? (
                            <FormControlLabel
                                control={<Checkbox checked={updateExisting} onChange={(e) => setUpdateExisting(e.target.checked)} />}
                                label={updateExistingLabel || 'עדכן לקוחות / ספקים קיימים (מזהה פנימי, מזהה חיצוני או התאמת שם)'}
                            />
                        ) : null}

                        {showIgnoreMustCheckbox ? (
                            <FormControlLabel
                                control={<Checkbox checked={ignoreMust} onChange={(e) => setIgnoreMust(e.target.checked)} />}
                                label='התעלם מעמודות חובה חסרות (טעינה גמישה)'
                            />
                        ) : null}

                        {withDate && <DialogActions><MyDatePicker date={date} setDate={setDate} /></DialogActions>}

                        {file ? (
                            <>
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    {fileRowCount == null
                                        ? `הקובץ ${file.name} נבחר. לא הצלחנו לספור שורות מראש - בדקו שהקובץ תקין.`
                                        : `הקובץ ${file.name} מכיל ${fileRowCount} שורות מידע (לא כולל כותרת). הפעולה תישלח לשרת ותבצע שינוי מיידי, ולא ניתן לבטל אותה אוטומטית.`}
                                </Alert>
                                <FormControlLabel
                                    sx={{ mt: 1 }}
                                    control={<Checkbox checked={confirmedLoad} onChange={(e) => setConfirmedLoad(e.target.checked)} />}
                                    label="אני מאשר/ת לטעון את הקובץ ולבצע את הפעולה"
                                />
                            </>
                        ) : null}
                    </DialogContent>

                    <DialogActions>
                        <Button onClick={handleOk} disabled={!file || !confirmedLoad} style={{ backgroundColor: 'green', color: 'white' }}>טען קובץ</Button>
                        <Button onClick={handleClose} style={{ backgroundColor: 'red', color: 'white' }}>ביטול</Button>
                    </DialogActions>
                </Dialog>
            )}

            <Dialog open={callbackDialog} onClose={() => setCallbackDialog(false)}>
                <DialogTitle>
                    {callbackData?.some(item => item.status === 0)
                        ? <span style={{ color: 'red' }}>הקובץ נטען עם שגיאות</span>
                        : <span style={{ color: 'green' }}>הקובץ נטען בהצלחה</span>}
                </DialogTitle>
                <DialogContent>
                    {callbackData?.length ? (
                        <Typography sx={{ mb: 1 }}>
                            סה״כ שורות: {callbackData.length} · הצליחו:{' '}
                            {callbackData.filter((i) => i.status === 1).length} · נכשלו / דולגו:{' '}
                            {callbackData.filter((i) => i.status === 0).length}
                        </Typography>
                    ) : null}
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>שורה</TableCell>
                                    <TableCell align='right'>סטטוס</TableCell>
                                    <TableCell align='right'>הערה</TableCell>
                                    <TableCell align='right'>ציון</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {callbackData?.map((item, i) => (
                                    <TableRow key={i}>
                                        <TableCell>{item.line}</TableCell>
                                        <TableCell align='right'>{item.status === 1 ? 'הצלחה' : 'כשלון'}</TableCell>
                                        <TableCell align='right'>{item.msg}</TableCell>
                                        <TableCell align='right'>{item.score ?? 0}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCallbackDialog(false)}>סגור</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default memo(DialogUploadFile);